/**
 * Context-bound seed wrapping (wrap_algorithm `X25519-HKDF-AES-GCM-v2`).
 *
 * `wrapSeed` (`X25519-AES-GCM`) uses the raw X25519 output as the AES key and binds
 * no context, so a wrapped seed for one workspace or recipient can be presented as
 * another. v2 derives the key with HKDF under a purpose-specific `info`, and passes
 * the workspace, purpose and recipient as AES-GCM additional data.
 *
 * Writers still produce `X25519-AES-GCM`; switching a writer to v2 needs every reader
 * of that wrap to call `unwrapSeedByAlgorithm` with the same context first.
 */
import { unwrapSeed } from './keyManagement.js';

export const WRAP_ALGORITHM_V1 = 'X25519-AES-GCM';
export const WRAP_ALGORITHM_LEGACY_RSA = 'RSA-OAEP-256';
export const WRAP_ALGORITHM_V2 = 'X25519-HKDF-AES-GCM-v2';

const FORMAT_VERSION = 2;

export interface SeedWrapContext {
  workspaceId: string;
  /** Who the copy is for, e.g. `device` or `recovery`; matches `wrap_type`. */
  purpose: string;
  /** Recipient identity: a device id, or the account for a recovery copy. */
  recipientId: string;
}

const encoder = new TextEncoder();

function contextBytes(context: SeedWrapContext): Uint8Array {
  return encoder.encode(JSON.stringify(['yada-seed-wrap', FORMAT_VERSION, context.workspaceId, context.purpose, context.recipientId]));
}

async function wrappingKey(sharedSecret: ArrayBuffer, ephemeralPublic: Uint8Array, purpose: string, usage: KeyUsage): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey('raw', sharedSecret, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: ephemeralPublic as unknown as BufferSource, info: encoder.encode(`yada-seed-wrap-v2/${purpose}`) },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    [usage]
  );
}

/** Layout: version (1) + ephemeral X25519 public key (32) + IV (12) + ciphertext and tag. */
export async function wrapSeedBound(seed: ArrayBuffer, recipientPublicKey: CryptoKey, context: SeedWrapContext): Promise<ArrayBuffer> {
  const ephemeral = await crypto.subtle.generateKey({ name: 'X25519' }, false, ['deriveBits']) as CryptoKeyPair;
  const ephemeralPublic = new Uint8Array(await crypto.subtle.exportKey('raw', ephemeral.publicKey));
  const shared = await crypto.subtle.deriveBits({ name: 'X25519', public: recipientPublicKey }, ephemeral.privateKey, 256);
  const key = await wrappingKey(shared, ephemeralPublic, context.purpose, 'encrypt');
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = new Uint8Array(await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: contextBytes(context) as unknown as BufferSource },
    key,
    seed
  ));
  const out = new Uint8Array(1 + 32 + 12 + ciphertext.byteLength);
  out[0] = FORMAT_VERSION;
  out.set(ephemeralPublic, 1);
  out.set(iv, 33);
  out.set(ciphertext, 45);
  return out.buffer;
}

export async function unwrapSeedBound(wrapped: ArrayBuffer, recipientPrivateKey: CryptoKey, context: SeedWrapContext): Promise<ArrayBuffer> {
  const bytes = new Uint8Array(wrapped);
  if (bytes.byteLength < 45 + 16 || bytes[0] !== FORMAT_VERSION) {
    throw new Error(`Wrapped seed is not ${WRAP_ALGORITHM_V2}`);
  }
  const ephemeralPublic = bytes.slice(1, 33);
  const peer = await crypto.subtle.importKey('raw', ephemeralPublic, { name: 'X25519' }, false, []);
  const shared = await crypto.subtle.deriveBits({ name: 'X25519', public: peer }, recipientPrivateKey, 256);
  const key = await wrappingKey(shared, ephemeralPublic, context.purpose, 'decrypt');
  return crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: bytes.slice(33, 45), additionalData: contextBytes(context) as unknown as BufferSource },
    key,
    bytes.slice(45)
  );
}

/**
 * Unwraps by the algorithm recorded with the wrapped seed, never by trying candidates.
 * `context` is required for v2 and ignored by the older formats, which bind nothing.
 */
export async function unwrapSeedByAlgorithm(
  wrapped: ArrayBuffer,
  privateKey: CryptoKey,
  wrapAlgorithm: string,
  context?: SeedWrapContext
): Promise<ArrayBuffer> {
  switch (wrapAlgorithm) {
    case WRAP_ALGORITHM_V2:
      if (!context) throw new Error(`${WRAP_ALGORITHM_V2} needs the workspace, purpose and recipient it was wrapped for`);
      return unwrapSeedBound(wrapped, privateKey, context);
    case WRAP_ALGORITHM_V1:
    case WRAP_ALGORITHM_LEGACY_RSA:
      return unwrapSeed(wrapped, privateKey);
    default:
      throw new Error(`Unsupported wrap algorithm: ${wrapAlgorithm}`);
  }
}
